import { TrendChart } from '@liza/ui/components/atoms/TrendChart';
import { Item, ItemContent, ItemTitle } from '@liza/ui/components/ui/item';
import { cn } from '@liza/ui/utils';
import { formatDate } from '@liza/ui/utils/date';
import { formatVitalValue } from '@liza/ui/utils/observation';
import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { ChevronRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ItemActions } from '../ui/item';

type Props = {
  className?: string;
  identifier: string;
  name: string;
  results?: WithId<Observation>[];
  latest?: WithId<Observation>;
  onSelect?: (identifier: string) => void;
  selected?: boolean;
};

export const ObservationItem = ({
  className,
  identifier,
  name,
  results,
  latest,
  onSelect,
  selected,
}: Props): React.ReactElement => {
  const { t } = useTranslation('records');

  const value = latest ? formatVitalValue(latest, t('unknown')) : '—';

  return (
    <Item
      size="sm"
      className={cn(
        onSelect && 'cursor-pointer hover:bg-accent/50',
        onSelect && selected && 'bg-accent text-accent-foreground hover:bg-accent',
        className,
      )}
      onClick={onSelect ? (): void => onSelect(identifier) : undefined}
      aria-current={onSelect && selected ? 'true' : undefined}
    >
      <ItemContent>
        <ItemTitle className="font-normal">{name}</ItemTitle>
        <span className="text-lg leading-none font-medium" aria-label={latest ? undefined : t('noData')}>
          {value}
        </span>
        {latest && (
          <span className={cn('text-xs', onSelect && selected ? 'text-accent-foreground/70' : 'text-muted-foreground')}>
            {formatDate(latest.effectiveDateTime)}
          </span>
        )}
      </ItemContent>
      {results && results.length > 0 && <TrendChart results={results} />}
      {onSelect && (
        <ItemActions>
          <ChevronRightIcon className="size-4 text-muted-foreground" />
        </ItemActions>
      )}
    </Item>
  );
};
